import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQueryClient, useMutation } from "@tanstack/react-query";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { useOrganization } from "@/contexts/OrganizationContext";
import { useAuth } from "@/contexts/AuthContext";
import { ChevronDown, Building2, Check, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { acceptOrganizationInvite, declineOrganizationInvite } from "@tickethall/core";
import { toast } from "@/hooks/use-toast";

export function OrganizationSwitcher({ className }: { className?: string }) {
  const { t } = useTranslation('producerCommon');
  const { activeContext, setActiveContext, organizations, refetch } = useOrganization();
  const { profile } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);

  const activeOrgs = organizations.filter((o) => o.member_status === "active");
  const pendingInvites = organizations.filter((o) => o.member_status === "pending");

  const acceptMutation = useMutation({
    mutationFn: (token: string) => acceptOrganizationInvite(token),
    onSuccess: (result) => {
      if (result.error) {
        toast({ title: t("orgSwitcher.toast.acceptError"), description: result.error, variant: "destructive" });
        return;
      }
      queryClient.invalidateQueries({ queryKey: ["my-organizations"] });
      refetch();
      toast({
        title: t("orgSwitcher.toast.acceptSuccessTitle"),
        description: t("orgSwitcher.toast.acceptSuccessDescription", {
          orgName: result.organization_name || t("orgSwitcher.toast.defaultOrgName"),
        }),
      });
    },
    onError: (err: any) => {
      toast({ title: t("orgSwitcher.toast.acceptError"), description: err.message || t("orgSwitcher.toast.genericErrorDescription"), variant: "destructive" });
    },
  });

  const declineMutation = useMutation({
    mutationFn: (token: string) => declineOrganizationInvite(token),
    onSuccess: (result) => {
      if (result.error) {
        toast({ title: t("orgSwitcher.toast.declineError"), description: result.error, variant: "destructive" });
        return;
      }
      queryClient.invalidateQueries({ queryKey: ["my-organizations"] });
      refetch();
      toast({ title: t("orgSwitcher.toast.declineSuccessTitle") });
    },
    onError: (err: any) => {
      toast({ title: t("orgSwitcher.toast.declineError"), description: err.message || t("orgSwitcher.toast.genericErrorDescription"), variant: "destructive" });
    },
  });

  const handleSelectPersonal = () => {
    setActiveContext({
      type: "personal",
      organizationId: null,
      organizationName: null,
      organizationSlug: null,
      organizationLogo: null,
    });
    setOpen(false);
  };

  const handleSelectOrg = (org: (typeof organizations)[0]) => {
    setActiveContext({
      type: "organization",
      organizationId: org.org_id,
      organizationName: org.org_name,
      organizationSlug: org.org_slug,
      organizationLogo: org.org_logo,
    });
    setOpen(false);
  };

  const initials = profile?.full_name
    ? profile.full_name
        .split(" ")
        .map((n) => n[0])
        .join("")
        .slice(0, 2)
        .toUpperCase()
    : t("orgSwitcher.trigger.initialsFallback");

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={cn(
            "relative h-9 gap-2 pl-2 pr-3 font-normal transition-colors",
            activeContext.type === "organization" && "border-primary/30 bg-primary/5",
            className
          )}
        >
          {pendingInvites.length > 0 && (
            <span className="absolute -top-1.5 -right-1.5 h-4 min-w-4 px-1 rounded-full bg-primary text-primary-foreground text-[10px] font-semibold flex items-center justify-center">
              {pendingInvites.length}
            </span>
          )}
          {activeContext.type === "organization" ? (
            <>
              <span className="text-muted-foreground text-xs">{t("orgSwitcher.trigger.orgLabel")}</span>
              <span className="max-w-[75px] sm:max-w-[140px] truncate text-sm">
                {activeContext.organizationName}
              </span>
            </>
          ) : (
            <>
              <span className="text-muted-foreground text-xs">{t("orgSwitcher.trigger.selfLabel")}</span>
              <span className="max-w-[75px] sm:max-w-[140px] truncate text-sm">
                {profile?.full_name || t("orgSwitcher.trigger.selfFallbackName")}
              </span>
            </>
          )}
          <ChevronDown className="h-3 w-3 text-muted-foreground/70" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-64">
        {pendingInvites.length > 0 && (
          <>
            <DropdownMenuLabel className="text-[11px] uppercase tracking-wider text-primary font-semibold">
              {t("orgSwitcher.invites.title")}
            </DropdownMenuLabel>
            {pendingInvites.map((invite) => (
              <div
                key={invite.org_id}
                className="flex items-center gap-2 px-3 py-2 m-1 rounded-md bg-primary/5 border border-primary/20"
              >
                <Avatar className="h-7 w-7 shrink-0">
                  {invite.org_logo ? (
                    <img src={invite.org_logo} alt="" className="h-full w-full object-cover" />
                  ) : (
                    <AvatarFallback className="text-[10px] bg-background border">{t("orgSwitcher.avatarFallback")}</AvatarFallback>
                  )}
                </Avatar>
                <p className="flex-1 min-w-0 text-xs font-medium truncate">{invite.org_name}</p>
                <Button
                  size="icon"
                  variant="ghost"
                  disabled={declineMutation.isPending}
                  onClick={() => invite.invite_token && declineMutation.mutate(invite.invite_token)}
                  className="h-6 w-6 shrink-0 text-muted-foreground hover:text-destructive"
                >
                  <X className="h-3.5 w-3.5" />
                </Button>
                <Button
                  size="icon"
                  disabled={acceptMutation.isPending}
                  onClick={() => invite.invite_token && acceptMutation.mutate(invite.invite_token)}
                  className="h-6 w-6 shrink-0 bg-primary text-primary-foreground hover:bg-primary/90"
                >
                  <Check className="h-3.5 w-3.5" />
                </Button>
              </div>
            ))}
            <DropdownMenuSeparator />
          </>
        )}

        <DropdownMenuLabel className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">
          {t("orgSwitcher.menu.postAsLabel")}
        </DropdownMenuLabel>

        <DropdownMenuItem
          onClick={handleSelectPersonal}
          className={cn(
            "gap-2 cursor-pointer transition-colors px-3 py-3",
            activeContext.type === "personal" && "bg-muted font-medium"
          )}
        >
          <Avatar className="h-8 w-8">
            <AvatarFallback className="text-[11px] bg-background border">{initials}</AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate">{profile?.full_name || t("orgSwitcher.menu.personalFallbackName")}</p>
            <p className="text-[11px] text-muted-foreground">{t("orgSwitcher.menu.personalSubtitle")}</p>
          </div>
          {activeContext.type === "personal" && (
            <span className="text-[10px] bg-background px-2 py-0.5 rounded border text-muted-foreground font-semibold">{t("orgSwitcher.menu.activeBadge")}</span>
          )}
        </DropdownMenuItem>

        {activeOrgs.length > 0 && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuLabel className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold px-3 pt-4">
              {t("orgSwitcher.menu.organizationsLabel")}
            </DropdownMenuLabel>
            {activeOrgs.map((org) => (
              <DropdownMenuItem
                key={org.org_id}
                onClick={() => handleSelectOrg(org)}
                className={cn(
                  "gap-2 cursor-pointer transition-colors px-3 py-3 m-1 rounded-md",
                  activeContext.organizationId === org.org_id && "bg-muted font-medium"
                )}
              >
                <Avatar className="h-8 w-8">
                  {org.org_logo ? (
                    <img src={org.org_logo} alt="" className="h-full w-full object-cover" />
                  ) : (
                    <AvatarFallback className="text-[11px] bg-background border">{t("orgSwitcher.avatarFallback")}</AvatarFallback>
                  )}
                </Avatar>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{org.org_name}</p>
                  <p className="text-[11px] text-muted-foreground capitalize">{org.org_role}</p>
                </div>
                {activeContext.organizationId === org.org_id && (
                  <span className="text-[10px] bg-background px-2 py-0.5 rounded border text-muted-foreground font-semibold">{t("orgSwitcher.menu.activeBadge")}</span>
                )}
              </DropdownMenuItem>
            ))}
          </>
        )}

        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={() => {
            setOpen(false);
            navigate("/produtor/organizacoes");
          }}
          className="gap-2 cursor-pointer font-medium"
        >
          <Building2 className="h-4 w-4 text-primary" />
          <span className="text-sm">{t("orgSwitcher.menu.myOrganizations")}</span>
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => {
            setOpen(false);
            navigate("/produtor/organizacoes/nova");
          }}
          className="gap-2 cursor-pointer text-muted-foreground"
        >
          <span className="text-sm">{t("orgSwitcher.menu.createOrganization")}</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
